// Small SVG bar charts (no chart library): bars or stacked bars per month, at most 260px tall,
// labelled y ticks, month labels and a legend. Each bar carries a native <title> tooltip with the
// exact figures, and every chart sits next to a table of the same numbers (the redesign's rule),
// so the chart itself is one labelled image to assistive technology. Geometry: ui/chart-model.ts.
import { useEffect, useRef, useState } from "preact/hooks";
import { barSlots, labelEvery, maxStack, niceScale, plotArea, stack, yFor, type Segment } from "./chart-model";
import { formatUsdCompact } from "./money";

export interface ChartSeries {
  id: string;
  label: string;
  /** The fill class from styles.css: `series-cost`, `series-profit`, `series-bar`, … */
  class: string;
}

export interface ChartBar {
  key: string;
  /** Under the bar ("Mar"). */
  label: string;
  /** The native tooltip: exact figures ("Mar 2026: billed $17,733.94, AWS cost …"). */
  tooltip: string;
  /** Bottom-up, in series order. */
  segments: readonly Segment[];
  /** Drawn lighter (an open month, a forecast). */
  muted?: boolean | undefined;
}

export interface BarChartProps {
  bars: readonly ChartBar[];
  series: readonly ChartSeries[];
  /** One sentence naming what the chart shows, for screen readers. */
  label: string;
  /** Plot height in px (the whole chart stays ≤ 260px). */
  height?: number | undefined;
  /** Y tick text; dollars by default. */
  formatTick?: ((value: number) => string) | undefined;
  /** Under the chart: where the same numbers are, what muted bars mean. */
  caption?: string | undefined;
  /** Hide the legend when a single series is named by the heading. */
  legend?: boolean | undefined;
}

/** The rendered width of the chart box, followed with a ResizeObserver (the SVG is sized by attributes). */
export function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? fallback);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

export function BarChart({ bars, series, label, height = 220, formatTick = formatUsdCompact, caption, legend = true }: BarChartProps) {
  const { ref, width } = useWidth(640);
  const totalHeight = Math.min(260, height);
  const scale = niceScale(maxStack(bars), 4);
  const tickWidth = Math.max(36, ...scale.ticks.map((tick) => formatTick(tick).length * 7 + 8));
  const plot = { width, height: totalHeight, left: tickWidth, right: 4, top: 8, bottom: 24 };
  const area = plotArea(plot);
  const slots = barSlots(bars.length, area);
  const every = labelEvery(bars.length, area.width);
  const classOf = new Map(series.map((s) => [s.id, s.class]));

  return (
    <div class="chart">
      {legend && series.length > 0 && (
        <ul class="chart-legend" aria-hidden="true">
          {series.map((s) => (
            <li key={s.id}>
              <svg class="legend-swatch" viewBox="0 0 10 10">
                <rect class={s.class} x="0" y="0" width="10" height="10" rx="2" />
              </svg>
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div ref={ref}>
        <svg viewBox={`0 0 ${width} ${totalHeight}`} width={width} height={totalHeight} role="img" aria-label={label}>
          {scale.ticks.map((tick) => {
            const y = yFor(tick, scale, area);
            return (
              <g key={tick}>
                <line class={tick === 0 ? "chart-baseline" : "chart-grid-line"} x1={area.x} x2={area.x + area.width} y1={y} y2={y} />
                <text class="chart-tick" x={area.x - 8} y={y} text-anchor="end" dominant-baseline="middle">
                  {formatTick(tick)}
                </text>
              </g>
            );
          })}
          {bars.map((bar, index) => {
            const slot = slots[index];
            if (!slot) return null;
            const rects = stack(bar.segments, scale, area);
            return (
              <g key={bar.key} class="chart-hit">
                <title>{bar.tooltip}</title>
                {/* An invisible full-height hit area so thin bars still show their tooltip. */}
                <rect class="chart-focus-ring" x={slot.x} y={area.y} width={slot.width} height={area.height} />
                {rects.map((rect) =>
                  rect.height > 0 ? (
                    <rect
                      key={rect.series}
                      class={`${classOf.get(rect.series) ?? "series-bar"}${bar.muted ? " chart-bar-muted" : ""}`}
                      x={slot.x}
                      y={rect.y}
                      width={slot.width}
                      height={rect.height}
                    />
                  ) : null,
                )}
                {index % every === 0 && (
                  <text class="chart-label" x={slot.center} y={area.y + area.height + 16} text-anchor="middle">
                    {bar.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {caption && <p class="chart-caption">{caption}</p>}
    </div>
  );
}
